import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/utils/date';
import { AudioLines, CalendarClock, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useAdminOrUserTtsFlowDetail } from '../api';
import { templateLabel, type TtsFlowFormValues, type TtsRow } from '../types';
import { TtsFlowPreviewPane } from './steps/TtsFlowPreviewPane';

interface Props {
  rowId: number;
  flowName: string;
  templateId: string;
  createdAt?: string;
  ownerUserId?: number;
  onClose: () => void;
}

/** tts_flow_json → TtsFlowFormValues. The stored payload wraps the row
 *  array as `ttsRows: { ttsList: [...] }` (and sometimes nests everything
 *  under `values`) — normalize both into the flat form shape the
 *  preview pane expects. */
function parseFlowValues(raw?: string): TtsFlowFormValues | null {
  if (!raw) return null;
  try {
    const outer = JSON.parse(raw) as Record<string, unknown>;
    const obj = (outer.values && typeof outer.values === 'object' ? outer.values : outer) as Record<
      string,
      unknown
    >;

    const rawRows = obj.ttsRows as unknown;
    const ttsRows = Array.isArray(rawRows)
      ? rawRows
      : Array.isArray((rawRows as { ttsList?: unknown })?.ttsList)
        ? (rawRows as { ttsList: TtsRow[] }).ttsList
        : [];

    return { ...obj, ttsRows } as TtsFlowFormValues;
  } catch {
    return null;
  }
}

export function TtsFlowPreviewModal({
  rowId,
  flowName,
  templateId,
  createdAt,
  ownerUserId,
  onClose,
}: Props) {
  const detail = useAdminOrUserTtsFlowDetail(rowId);
  // Patched copy lives only in modal state — synthesized preview URLs are
  // session-local, never written back to the flow.
  const [patched, setPatched] = useState<TtsFlowFormValues | null>(null);

  const parsed = useMemo(() => parseFlowValues(detail.data?.ttsFlow), [detail.data]);
  const values = patched ?? parsed;

  const onPatchRow = useCallback(
    (index: number, patch: Partial<TtsRow>) => {
      setPatched((prev) => {
        const base = prev ?? parsed;
        if (!base) return prev;
        return {
          ...base,
          ttsRows: base.ttsRows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
        };
      });
    },
    [parsed],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg gap-0 overflow-hidden rounded-xl p-0">
        {/* Header band */}
        <div className="border-b bg-gradient-to-br from-primary/5 via-transparent to-transparent px-5 py-4">
          <DialogHeader className="space-y-0 text-left">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                <AudioLines className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate leading-tight">{flowName}</DialogTitle>
                <DialogDescription className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <Badge variant="outline" className="font-normal">
                    {templateLabel(templateId)}
                  </Badge>
                  {createdAt && (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <CalendarClock className="h-3 w-3" />
                      {formatDateTime(createdAt)}
                    </span>
                  )}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="max-h-[calc(85vh-9rem)] overflow-y-auto px-5 py-4">
          {detail.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Loading flow definition…</p>
            </div>
          ) : !values ? (
            <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed py-12 text-center">
              <AudioLines className="mb-1 h-6 w-6 text-muted-foreground/50" />
              <p className="text-sm font-medium text-foreground">Preview unavailable</p>
              <p className="text-xs text-muted-foreground">Could not load the flow definition.</p>
            </div>
          ) : (
            <>
              <div className="mb-3 text-xs text-muted-foreground">
                Language : {values.language || '—'} · {values.gender || '—'}
              </div>
              <TtsFlowPreviewPane
                values={values}
                onPatchRow={onPatchRow}
                ownerUserId={ownerUserId}
              />
            </>
          )}
        </div>
        <div className="border-t bg-muted/30 px-5 py-2.5">
          <span className="font-mono text-[11px] text-muted-foreground">Flow #{rowId}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
